import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

import {
  canonicalTreeExternalEvidenceSnapshotMatchesGraph,
  createUnavailableCanonicalTreeExternalEvidenceSnapshot,
  markCanonicalTreeExternalEvidenceSnapshotStale,
  type CanonicalTreeExternalEvidenceRefreshReason,
  type CanonicalTreeExternalEvidenceSnapshot,
} from './CanonicalTreeExternalEvidenceSnapshot';

export type CanonicalTreeExternalEvidenceContextAdoptionAction =
  | 'INITIALIZE'
  | 'REPLACE'
  | 'RETAIN'
  | 'INVALIDATE';

export interface CanonicalTreeExternalEvidenceContextState {
  readonly ownerUserId:
    string | null;

  readonly members:
    readonly GraphMember[];

  readonly snapshot:
    CanonicalTreeExternalEvidenceSnapshot;

  readonly lastAttempt:
    CanonicalTreeExternalEvidenceSnapshot | null;

  readonly action:
    CanonicalTreeExternalEvidenceContextAdoptionAction;

  readonly gatewayTransport:
    {
      readonly spousePartnerEdges?:
        CanonicalTreeExternalEvidenceSnapshot['spousePartnerEdges'];

      readonly relationshipHistory?:
        CanonicalTreeExternalEvidenceSnapshot['relationshipHistory'];
    } | null;
}

const freezeMembers = (
  members:
    readonly GraphMember[],
): readonly GraphMember[] =>
  Object.freeze([
    ...members,
  ]);

const createGatewayTransport = (
  input: {
    readonly snapshot:
      CanonicalTreeExternalEvidenceSnapshot;

    readonly ownerUserId:
      string | null;

    readonly members:
      readonly GraphMember[];

    readonly enabled:
      boolean;
  },
): CanonicalTreeExternalEvidenceContextState['gatewayTransport'] => {
  if (
    !input.enabled ||
    !canonicalTreeExternalEvidenceSnapshotMatchesGraph({
      snapshot:
        input.snapshot,

      ownerUserId:
        input.ownerUserId,

      members:
        input.members,
    })
  ) {
    return null;
  }

  return Object.freeze({
    spousePartnerEdges:
      input.snapshot
        .spousePartnerEdges,

    relationshipHistory:
      input.snapshot
        .relationshipHistory,
  });
};

export const createCanonicalTreeExternalEvidenceContextState = (
  input?: {
    readonly gatewayTransportEnabled?:
      boolean;
  },
): CanonicalTreeExternalEvidenceContextState => {
  const snapshot =
    createUnavailableCanonicalTreeExternalEvidenceSnapshot();

  return Object.freeze({
    ownerUserId:
      null,

    members:
      Object.freeze([]),

    snapshot,

    lastAttempt:
      null,

    action:
      'INITIALIZE',

    gatewayTransport:
      createGatewayTransport({
        snapshot,

        ownerUserId:
          null,

        members:
          [],

        enabled:
          input
            ?.gatewayTransportEnabled ??
          false,
      }),
  });
};

export const adoptCanonicalTreeExternalEvidenceSnapshot = (
  input: {
    readonly current:
      CanonicalTreeExternalEvidenceContextState;

    readonly ownerUserId:
      string;

    readonly members:
      readonly GraphMember[];

    readonly candidate:
      CanonicalTreeExternalEvidenceSnapshot;

    readonly gatewayTransportEnabled?:
      boolean;
  },
): CanonicalTreeExternalEvidenceContextState => {
  const members =
    freezeMembers(
      input.members,
    );

  const candidateMatches =
    canonicalTreeExternalEvidenceSnapshotMatchesGraph({
      snapshot:
        input.candidate,

      ownerUserId:
        input.ownerUserId,

      members,
    });

  const currentMatches =
    canonicalTreeExternalEvidenceSnapshotMatchesGraph({
      snapshot:
        input.current.snapshot,

      ownerUserId:
        input.ownerUserId,

      members,
    });

  const snapshot =
    candidateMatches
      ? input.candidate
      : currentMatches
        ? input.current.snapshot
        : input.current.snapshot.status ===
              'LOADED'
          ? markCanonicalTreeExternalEvidenceSnapshotStale({
              snapshot:
                input.current.snapshot,

              refreshReason:
                input.current.ownerUserId !==
                input.ownerUserId
                  ? 'GRAPH-OWNER-CHANGED'
                  : 'MEMBERS-CHANGED',
            })
          : input.current.snapshot;

  const action:
    CanonicalTreeExternalEvidenceContextAdoptionAction =
      candidateMatches
        ? 'REPLACE'
        : currentMatches
          ? 'RETAIN'
          : snapshot.status ===
              'STALE'
            ? 'INVALIDATE'
            : 'RETAIN';

  return Object.freeze({
    ownerUserId:
      input.ownerUserId,

    members,

    snapshot,

    lastAttempt:
      input.candidate,

    action,

    gatewayTransport:
      createGatewayTransport({
        snapshot,

        ownerUserId:
          input.ownerUserId,

        members,

        enabled:
          input
            .gatewayTransportEnabled ??
          false,
      }),
  });
};

export const invalidateCanonicalTreeExternalEvidenceContextState = (
  input: {
    readonly current:
      CanonicalTreeExternalEvidenceContextState;

    readonly refreshReason:
      CanonicalTreeExternalEvidenceRefreshReason;

    readonly gatewayTransportEnabled?:
      boolean;
  },
): CanonicalTreeExternalEvidenceContextState => {
  const snapshot =
    input.current.snapshot.status ===
      'LOADED'
      ? markCanonicalTreeExternalEvidenceSnapshotStale({
          snapshot:
            input.current.snapshot,

          refreshReason:
            input.refreshReason,
        })
      : input.current.snapshot;

  return Object.freeze({
    ...input.current,

    snapshot,

    action:
      snapshot.status ===
        'STALE'
        ? 'INVALIDATE'
        : 'RETAIN',

    gatewayTransport:
      createGatewayTransport({
        snapshot,

        ownerUserId:
          input.current.ownerUserId,

        members:
          input.current.members,

        enabled:
          input
            .gatewayTransportEnabled ??
          false,
      }),
  });
};
